import React from 'react';
import { Card, CardContent } from '@/components/ui/Card';
import { InvoiceMetricsSummary } from '@/types/domain/invoice-management';
import { formatCurrency } from '@/lib/utils';
import {
  Receipt,
  CheckCircle2,
  AlertTriangle,
  FileQuestion,
  Clock,
  DollarSign,
} from 'lucide-react';

interface InvoiceKpiCardsProps {
  metrics: InvoiceMetricsSummary;
  onSelectFilter?: (filterType: string, value: string) => void;
}

export const InvoiceKpiCards: React.FC<InvoiceKpiCardsProps> = ({ metrics, onSelectFilter }) => {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3.5 font-poppins">
      {/* Card 1: Total Invoices */}
      <Card
        onClick={() => onSelectFilter?.('status', 'ALL')}
        className="rounded-2xl border-border bg-card hover:border-primary/40 transition-all cursor-pointer shadow-2xs group"
      >
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-extrabold tracking-wider uppercase text-foreground-secondary">
              Total Invoices
            </span>
            <div className="p-1.5 rounded-lg bg-blue-500/10 text-blue-600 group-hover:scale-110 transition-transform">
              <Receipt className="h-4 w-4" />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-black text-foreground">{metrics.totalInvoices}</span>
            <span className="text-[10px] text-foreground-muted font-bold">
              {formatCurrency(metrics.totalInvoiceValue)}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Card 2: 3-Way Matched */}
      <Card
        onClick={() => onSelectFilter?.('matchingStatus', 'MATCHED')}
        className="rounded-2xl border-border bg-card hover:border-emerald-500/40 transition-all cursor-pointer shadow-2xs group"
      >
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-extrabold tracking-wider uppercase text-emerald-600">
              3-Way Matched
            </span>
            <div className="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-600 group-hover:scale-110 transition-transform">
              <CheckCircle2 className="h-4 w-4" />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-black text-emerald-600">{metrics.matchedCount}</span>
            <span className="text-[10px] text-emerald-600/80 font-bold">clean match</span>
          </div>
        </CardContent>
      </Card>

      {/* Card 3: Open Exceptions */}
      <Card
        onClick={() => onSelectFilter?.('status', 'EXCEPTION')}
        className="rounded-2xl border-border bg-card hover:border-rose-500/40 transition-all cursor-pointer shadow-2xs group"
      >
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-extrabold tracking-wider uppercase text-rose-600">
              Exceptions
            </span>
            <div className="p-1.5 rounded-lg bg-rose-500/10 text-rose-600 group-hover:scale-110 transition-transform">
              <AlertTriangle className="h-4 w-4" />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-black text-rose-600">{metrics.exceptionCount}</span>
            <span className="text-[10px] text-rose-600/80 font-bold">requires review</span>
          </div>
        </CardContent>
      </Card>

      {/* Card 4: Missing GRN */}
      <Card
        onClick={() => onSelectFilter?.('matchingStatus', 'MISSING_GRN')}
        className="rounded-2xl border-border bg-card hover:border-amber-500/40 transition-all cursor-pointer shadow-2xs group"
      >
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-extrabold tracking-wider uppercase text-amber-600">
              Missing GRN
            </span>
            <div className="p-1.5 rounded-lg bg-amber-500/10 text-amber-600 group-hover:scale-110 transition-transform">
              <FileQuestion className="h-4 w-4" />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-black text-amber-600">{metrics.missingGrnCount}</span>
            <span className="text-[10px] text-amber-600/80 font-bold">dock pending</span>
          </div>
        </CardContent>
      </Card>

      {/* Card 5: Overdue Invoices */}
      <Card
        onClick={() => onSelectFilter?.('isOverdue', 'true')}
        className="rounded-2xl border-border bg-card hover:border-rose-500/40 transition-all cursor-pointer shadow-2xs group"
      >
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-extrabold tracking-wider uppercase text-rose-600">
              Overdue AP
            </span>
            <div className="p-1.5 rounded-lg bg-rose-500/10 text-rose-600 group-hover:scale-110 transition-transform">
              <Clock className="h-4 w-4" />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-black text-rose-600">{metrics.overdueCount}</span>
            <span className="text-[10px] text-rose-600/80 font-bold">past due date</span>
          </div>
        </CardContent>
      </Card>

      {/* Card 6: Ready for Payment */}
      <Card
        onClick={() => onSelectFilter?.('paymentStatus', 'READY_FOR_PAYMENT')}
        className="rounded-2xl border-border bg-card hover:border-purple-500/40 transition-all cursor-pointer shadow-2xs group"
      >
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-extrabold tracking-wider uppercase text-purple-600">
              Ready for Payment
            </span>
            <div className="p-1.5 rounded-lg bg-purple-500/10 text-purple-600 group-hover:scale-110 transition-transform">
              <DollarSign className="h-4 w-4" />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-black text-purple-700">{metrics.readyForPaymentCount}</span>
            <span className="text-[10px] text-purple-600/80 font-bold">cleared for run</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
